<template>
  <el-main data-cy="game-list-page" class="games-list-page">
    <section class="games-list-search">
      <VdInput v-model="searchTerm" class="search-input" placeholder="Looking for something...">
        <template #prepend>
          <VdIcon store="fa" name="search" />
        </template>
      </VdInput>
      <vd-button
        v-if="canWriteGames"
        data-cy="import-game-button"
        type="primary"
        icon-left="fas:cloud-arrow-up"
        @click="onGameImport"
      >
        <span>Import a Game</span>
      </vd-button>
    </section>
    <div v-loading.fullscreen="loading" class="games-list">
      <GamesListItem v-for="game in filteredGames" :key="game.id" :value="game" class="game" />
    </div>
  </el-main>
</template>

<script lang="ts">
/**
 * This component list all the games set up for VoodooTune.
 * It allows to filter the games by name or bundle ids.
 * A link to the import page is displayed to allow importing a game not in the list.
 * Finally, if no games are set up for VoodooTune (ie: the list is empty), the component redirect to the import page.
 */

import { VdInput } from "@voodoo.io/voodoo-ui";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { defineComponent, computed } from "vue";
import { useRouter } from "vue-router";

import { useGamesListPageQuery } from "@/graphql";
import useSearchFilter from "@/hooks/search-filter";
import usePermissions from "@/hooks/use-permissions";
import GamesListItem from "@/pages/games/GamesList/components/GamesListItem.vue";
import links from "@/router/links";

export default defineComponent({
  name: "GamesListPage",

  components: {
    GamesListItem,
    VdIcon,
    VdInput,
  },

  setup() {
    const router = useRouter();
    const { canWriteGames } = usePermissions();

    /**
     * Fetch the games set up for VoodooTune from the servers.
     */
    const { result, loading } = useGamesListPageQuery({ fetchPolicy: "network-only" });

    const games = computed(() => result.value?.games ?? []);

    /**
     * Build the filtered games list from the search term.
     */
    const { searchTerm, filteredItems: filteredGames } = useSearchFilter(games, [
      "id",
      "name",
      "androidBundleId",
      "iosBundleId",
    ]);

    /**
     * Some links
     */
    const onGameImport = () => {
      router.push({ name: links.games.import });
    };

    return {
      filteredGames,
      searchTerm,
      onGameImport,
      loading,
      canWriteGames,
    };
  },
});
</script>

<style lang="scss" scoped>
.games-list-page {
  .games-list-search {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 40px;

    .search-input {
      width: 400px;
      margin-right: 10px;
    }

    .games-list-choice {
      margin: 0 20px;
      font-size: 16px;
      color: $grey;
    }
  }

  .games-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, 180px);
    grid-gap: 20px;
    justify-content: center;
  }
}
</style>
